import React from "react";
import { Document, Page, Text } from "@react-pdf/renderer";
import { ReportHeader } from "../components/charting/pdf/shared/ReportHeader";
import { ReportFooter } from "../components/charting/pdf/shared/ReportFooter";
import { ReportTable } from "../components/charting/pdf/shared/ReportTable";
import { StrikeZoneGrid } from "../components/charting/pdf/shared/StrikeZoneGrid";
import { reportStyles } from "../components/charting/pdf/shared/styles";
import { getPitchers } from "../components/charting/pdf/shared/utils";
import { processPitchDataForAdvance } from "../components/charting/pdf/shared/pitchProcessing";

export const PitcherAdvanceReport = ({ charts = [] }) => {
  const pitchers = getPitchers(charts);
  if (!charts.length || !pitchers.length) {
    return (
      <Document>
        <Page size="A4" style={reportStyles.page}>
          <Text>No data available to generate the report.</Text>
        </Page>
      </Document>
    );
  }

  const pages = pitchers
    .flatMap((pitcherName) => {
      const pitcherPitches = charts.flatMap((chart) =>
        chart.pitches.filter((pitch) => pitch.pitcher?.name === pitcherName)
      );

      const overallMetrics = processPitchDataForAdvance(pitcherPitches);
      const overallTableRows = Object.entries(overallMetrics).map(([type, data]) => ({
        cells: [
          { value: type.charAt(0) + type.slice(1).toLowerCase() },
          { value: data.count },
          { value: `${data.usage}%` },
          { value: data.avgVelo },
          { value: data.maxVelo },
          { value: `${data.zoneRate}%` },
          { value: `${data.strikeRate}%` },
          { value: `${data.cswRate}%` },
          { value: `${data.hardHitRate}%` },
        ],
      }));

      const overallStrikeZoneData = Object.entries(overallMetrics).reduce(
        (acc, [type, data]) => {
          acc[type] = { pitches: data.pitches };
          return acc;
        },
        {}
      );

      const overallPage = (
        <Page
          key={`${pitcherName}-overall`}
          size="A4"
          orientation="portrait"
          style={reportStyles.page}
        >
          <ReportHeader
            title={`${pitcherName} - Advance Report`}
            subtitle="Overall"
          />

          <ReportTable
            headers={[
              "Pitch Type",
              "Count",
              "Usage %",
              "Avg Velo",
              "Max Velo",
              "Zone%",
              "Strike%",
              "CSW%",
              "HardHit%",
            ]}
            rows={overallTableRows}
            cellStyles={{ header: Array(9).fill({ fontSize: 9 }) }}
          />

          <StrikeZoneGrid pitchData={overallStrikeZoneData} />

          <ReportFooter />
        </Page>
      );

      const splitPages = ["left", "right"].map((batterHand) => {
        const handFilteredPitches = pitcherPitches.filter(
          (pitch) => pitch.batter?.batHand?.toLowerCase() === batterHand
        );

        if (!handFilteredPitches.length) return null;

        const pitchMetrics = processPitchDataForAdvance(handFilteredPitches);

        const tableRows = Object.entries(pitchMetrics).map(([type, data]) => ({
          cells: [
            { value: type.charAt(0) + type.slice(1).toLowerCase() },
            { value: data.count },
            { value: `${data.usage}%` },
            { value: data.avgVelo },
            { value: data.maxVelo },
            { value: `${data.zoneRate}%` },
            { value: `${data.strikeRate}%` },
            { value: `${data.cswRate}%` },
            { value: `${data.hardHitRate}%` },
          ],
        }));

        const strikeZoneData = Object.entries(pitchMetrics).reduce(
          (acc, [type, data]) => {
            acc[type] = { pitches: data.pitches };
            return acc;
          },
          {}
        );

        return (
          <Page
            key={`${pitcherName}-${batterHand}`}
            size="A4"
            orientation="portrait"
            style={reportStyles.page}
          >
            <ReportHeader
              title={`${pitcherName} - Advance Report`}
              subtitle={`vs ${batterHand === "left" ? "LHH" : "RHH"}`}
            />

            <ReportTable
              headers={[
                  "Pitch Type",
                  "Count",
                  "Usage %",
                  "Avg Velo",
                  "Max Velo",
                  "Zone%",
                "Strike%",
                  "CSW%",
                  "HardHit%",
              ]}
              rows={tableRows}
              cellStyles={{ header: Array(9).fill({ fontSize: 9 }) }}
            />

            <StrikeZoneGrid pitchData={strikeZoneData} />

            <ReportFooter />
          </Page>
        );
      }).filter(Boolean);

      return [overallPage, ...splitPages];
    })
    .flat()
    .filter(Boolean);

  return <Document>{pages}</Document>;
};

